import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { Loader2, Plus, Edit, Settings, Truck, Car, Save, Check, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'performance', label: 'Rendimiento' },
  { value: 'emission', label: 'Emisiones' },
  { value: 'limiter', label: 'Limitadores' },
  { value: 'diagnostic', label: 'Diagnóstico' },
  { value: 'comfort', label: 'Confort' },
  { value: 'other', label: 'Otro' },
];

const AdminServicesPage = () => {
  const { token } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editService, setEditService] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: '', price: '', industrial_price: '', category: 'other' });
  const [saving, setSaving] = useState({});
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchServices(); }, []);

  const fetchServices = async () => {
    try {
      const res = await axios.get(`${API}/admin/services`, { headers });
      setServices(res.data.services || []);
    } catch { toast.error('Error al cargar servicios'); }
    finally { setLoading(false); }
  };

  const saveService = async () => {
    if (!form.name || !form.price) return toast.error('Completa nombre y precio');
    try {
      const fd = new FormData();
      fd.append('name', form.name);
      fd.append('price', parseFloat(form.price));
      fd.append('industrial_price', form.industrial_price ? parseFloat(form.industrial_price) : 0);
      if (editService) {
        fd.append('active', form.active !== false);
        fd.append('active_turismo', form.active_turismo !== false);
        fd.append('active_industrial', form.active_industrial !== false);
        await axios.put(`${API}/admin/services/${editService.id}`, fd, { headers });
        toast.success('Servicio actualizado');
      } else {
        fd.append('category', form.category);
        await axios.post(`${API}/admin/services`, fd, { headers });
        toast.success('Servicio creado');
      }
      setEditService(null); setShowNew(false);
      setForm({ name: '', price: '', industrial_price: '', category: 'other' });
      await fetchServices();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const quickToggle = async (serviceId, field) => {
    const s = services.find(sv => sv.id === serviceId);
    if (!s) return;
    setSaving(prev => ({...prev, [serviceId + field]: true}));
    try {
      const fd = new FormData();
      fd.append('name', s.name);
      fd.append('price', s.price);
      fd.append('industrial_price', s.industrial_price || 0);
      fd.append('active', field === 'active' ? !(s.active !== false) : (s.active !== false));
      fd.append('active_turismo', field === 'active_turismo' ? !(s.active_turismo !== false) : (s.active_turismo !== false));
      fd.append('active_industrial', field === 'active_industrial' ? !(s.active_industrial !== false) : (s.active_industrial !== false));
      await axios.put(`${API}/admin/services/${serviceId}`, fd, { headers });
      await fetchServices();
    } catch { toast.error('Error al actualizar'); }
    finally { setSaving(prev => ({...prev, [serviceId + field]: false})); }
  };

  const openEdit = (s) => {
    setEditService(s);
    setForm({
      name: s.name,
      price: s.price.toString(),
      industrial_price: (s.industrial_price || '').toString(),
      category: s.category || 'other',
      active: s.active !== false,
      active_turismo: s.active_turismo !== false,
      active_industrial: s.active_industrial !== false,
    });
  };

  const openNew = () => {
    setEditService(null);
    setForm({ name: '', price: '', industrial_price: '', category: 'other' });
    setShowNew(true);
  };

  const TogglePill = ({ active, loading: isLoading, onClick, label }) => (
    <button onClick={onClick} disabled={isLoading}
      className={`text-xs px-2.5 py-1 rounded-full cursor-pointer transition-colors ${active ? 'text-green-400 bg-green-500/10 hover:bg-green-500/20' : 'text-red-400 bg-red-500/10 hover:bg-red-500/20'}`}
      data-testid={label}>
      {isLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : active ? 'ON' : 'OFF'}
    </button>
  );

  return (
    <Layout>
      <div className="max-w-6xl mx-auto" data-testid="admin-services-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-3xl font-bold text-white">SERVICIOS</h1>
            <p className="text-zinc-400 mt-1">Gestiona los servicios disponibles y sus precios</p>
          </div>
          <Button onClick={openNew} className="bg-primary hover:bg-primary/90">
            <Plus className="w-4 h-4 mr-2" />Nuevo Servicio
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>
        ) : (
          <div className="bg-surface border border-white/10 rounded-md overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10 bg-surface-highlight">
                  <th className="text-left p-4 text-xs text-zinc-500 uppercase">Servicio</th>
                  <th className="text-left p-4 text-xs text-zinc-500 uppercase">Categoría</th>
                  <th className="text-center p-4 text-xs text-zinc-500 uppercase">
                    <div className="flex items-center justify-center gap-1"><Car className="w-3 h-3" /> Turismo</div>
                  </th>
                  <th className="text-center p-4 text-xs text-zinc-500 uppercase">
                    <div className="flex items-center justify-center gap-1"><Truck className="w-3 h-3 text-orange-400" /> Industrial</div>
                  </th>
                  <th className="text-right p-4 text-xs text-zinc-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {services.map(s => (
                  <tr key={s.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="p-4 text-white font-medium">{s.name}</td>
                    <td className="p-4 text-zinc-400 text-sm capitalize">{CATEGORIES.find(c => c.value === s.category)?.label || s.category}</td>
                    <td className="p-4 text-center">
                      <div className="flex items-center justify-center gap-3">
                        <span className="font-mono text-primary font-bold">{s.price}€</span>
                        <TogglePill
                          active={s.active_turismo !== false}
                          loading={saving[s.id + 'active_turismo']}
                          onClick={() => quickToggle(s.id, 'active_turismo')}
                          label={`turismo-toggle-${s.id}`}
                        />
                      </div>
                    </td>
                    <td className="p-4 text-center">
                      <div className="flex items-center justify-center gap-3">
                        <span className={`font-mono font-bold ${s.industrial_price ? 'text-orange-400' : 'text-zinc-600'}`}>
                          {s.industrial_price ? `${s.industrial_price}€` : '—'}
                        </span>
                        <TogglePill
                          active={s.active_industrial !== false}
                          loading={saving[s.id + 'active_industrial']}
                          onClick={() => quickToggle(s.id, 'active_industrial')}
                          label={`industrial-toggle-${s.id}`}
                        />
                      </div>
                    </td>
                    <td className="p-4 text-right">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(s)} className="text-zinc-400 hover:text-white">
                        <Edit className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!editService || showNew} onOpenChange={() => { setEditService(null); setShowNew(false); }}>
        <DialogContent className="bg-surface border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Settings className="w-5 h-5 text-primary" />
              {editService ? 'EDITAR SERVICIO' : 'NUEVO SERVICIO'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-zinc-400 text-sm mb-1 block">Nombre</label>
              <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Nombre del servicio"
                className="bg-surface-highlight border-white/10 text-white" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-zinc-400 text-sm mb-1 flex items-center gap-1"><Car className="w-3 h-3" /> Precio Turismo (€)</label>
                <Input value={form.price} onChange={e => setForm({...form, price: e.target.value})} placeholder="100" type="number" step="0.01"
                  className="bg-surface-highlight border-white/10 text-white" />
              </div>
              <div>
                <label className="text-orange-400 text-sm mb-1 flex items-center gap-1"><Truck className="w-3 h-3" /> Precio Industrial (€)</label>
                <Input value={form.industrial_price} onChange={e => setForm({...form, industrial_price: e.target.value})} placeholder="400" type="number" step="0.01"
                  className="bg-surface-highlight border-white/10 text-white" />
              </div>
            </div>
            {!editService && (
              <div>
                <label className="text-zinc-400 text-sm mb-1 block">Categoría</label>
                <Select value={form.category} onValueChange={v => setForm({...form, category: v})}>
                  <SelectTrigger className="bg-surface-highlight border-white/10 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-surface border-white/10">
                    {CATEGORIES.map(c => (
                      <SelectItem key={c.value} value={c.value} className="text-white hover:bg-white/10">{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Button onClick={saveService} className="w-full bg-primary hover:bg-primary/90">
              <Save className="w-4 h-4 mr-2" />{editService ? 'Guardar Cambios' : 'Crear Servicio'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default AdminServicesPage;
